import WhatsappIcon from '@mui/icons-material/WhatsApp'
import { ToggleButtonProps } from '@mui/material/ToggleButton'

import { cannot } from '@/app/helpers'
import { Q_ACTIONS_DESCRIPTIONS } from '@/app/module/campaigns/definitions'
import { CampaignTemplateType, LocalActionType, PartFamily } from '@/app/module/campaigns/types'
import { SelectOption } from '@/app/types'

import { ActionListContext, getValidActionsForCampaignType } from './list'
import { ActionContext } from './types'

export type ActionOptionColor = ToggleButtonProps['color']

export type BaseActionOption = SelectOption<LocalActionType> & {
  icon: ActionIcon
  color?: ActionOptionColor
  final?: boolean
}

export type ActionOption = BaseActionOption & {
  description: string
  disabled?: boolean
  disabledReason?: string
  icon: ActionIcon
}

const RUN_QUOTA_DISABLED_REASON = 'No quotas are set up for this campaign. Add one to enable this action.'

export function getActionOptionsForCampaign(ctx: ActionListContext): ActionOption[] {
  const isRunQuotaDisabled = ctx.hasQuotas === false
  const options = getValidActionsForCampaignType(isRunQuotaDisabled ? { ...ctx, hasQuotas: true } : ctx)
  const coloredOptions = addColorToActions(getActionOptions(options), ctx.campaignType)
  const withFinal = addFinalToActions(coloredOptions, ctx)
  const withDesc = addDescriptionToActions(withFinal, ctx.actionNamespace)

  return withDesc.map((option) =>
    option.value === LocalActionType.RunQuota && isRunQuotaDisabled
      ? { ...option, disabled: true, disabledReason: RUN_QUOTA_DISABLED_REASON }
      : option,
  )
}

export const getActionDescription = (action: BaseActionOption, context: ActionContext = ActionContext.Default) => {
  const actionDescriptions = Q_ACTIONS_DESCRIPTIONS as Record<string, Record<string, string>>
  const desc = actionDescriptions?.[action.value]?.[context]
  return desc || actionDescriptions?.[action.value]?.default || ''
}

export const addDescriptionToActions = (
  actions: Array<BaseActionOption>,
  context: ActionContext = ActionContext.Default,
) =>
  actions.map((action) => ({
    ...action,
    description: getActionDescription(action, context),
  }))

const getActionColor = (type: LocalActionType, campaignType: CampaignTemplateType): ActionOptionColor | undefined => {
  if (type === LocalActionType.Hangup && campaignType === CampaignTemplateType.Drip) {
    return undefined
  }

  switch (type) {
    case LocalActionType.FinishFlow:
    case LocalActionType.Hangup:
      return 'error'
    default:
      return undefined
  }
}

const addColorToActions = (actions: Array<BaseActionOption>, campaignType: CampaignTemplateType) =>
  actions.map((action) => ({
    ...action,
    color: getActionColor(action.value, campaignType),
  }))

export const isFinalActionForContext = (type: LocalActionType, ctx: ActionListContext): boolean => {
  switch (type) {
    case LocalActionType.Jump:
    case LocalActionType.FinishFlow:
      return true
    case LocalActionType.Hangup: {
      // Hangup is NOT a final action for Drip campaign voice questions.
      if (ctx.campaignType === CampaignTemplateType.Drip && 'partType' in ctx && ctx.partType === PartFamily.Voice) {
        return false
      }
      return true
    }
    default:
      return false
  }
}

const addFinalToActions = (actions: Array<BaseActionOption>, ctx: ActionListContext) =>
  actions.map((action) => ({
    ...action,
    final: isFinalActionForContext(action.value, ctx),
  }))

type ActionIcon = string | (() => JSX.Element)

export const getActionOptions = (actionTypes: LocalActionType[]): Array<BaseActionOption> =>
  actionTypes.map((type) => {
    switch (type) {
      case LocalActionType.API:
        return {
          label: 'Make API request (webhook)',
          value: LocalActionType.API,
          icon: 'settings_remote',
        }
      case LocalActionType.FinishFlow:
        return {
          label: 'End campaign for contact',
          value: LocalActionType.FinishFlow,
          icon: 'stop',
        }
      case LocalActionType.Hangup:
        return {
          label: 'Hangup',
          value: LocalActionType.Hangup,
          icon: 'call_end',
        }
      case LocalActionType.Pickup:
        return { label: 'Pickup', value: LocalActionType.Pickup, icon: 'call' }
      case LocalActionType.Jump:
        return {
          label: 'Jump to part',
          value: LocalActionType.Jump,
          icon: 'account_tree',
        }
      case LocalActionType.Replay:
        return {
          label: 'Replay the question',
          value: LocalActionType.Replay,
          icon: 'replay_circle_filled',
        }
      case LocalActionType.Reopen:
        return {
          label: 'Resume latest subscription',
          value: LocalActionType.Reopen,
          icon: 'restart_alt',
        }
      case LocalActionType.Resend:
        return {
          label: 'Resend the question',
          value: LocalActionType.Resend,
          icon: 'send',
        }
      case LocalActionType.RunQuota:
        return {
          label: 'Mark Survey as Completed',
          value: LocalActionType.RunQuota,
          icon: 'assignment_turned_in',
        }
      case LocalActionType.SendEmail:
        return {
          label: 'Send Email',
          value: LocalActionType.SendEmail,
          icon: 'email',
        }
      case LocalActionType.SendSMS:
        return {
          label: 'Send SMS to contact',
          value: LocalActionType.SendSMS,
          icon: 'sms',
        }
      case LocalActionType.SendSMSToPhoneNumbers:
        return {
          label: 'Send SMS to someone else',
          value: LocalActionType.SendSMSToPhoneNumbers,
          icon: 'send_to_mobile',
        }
      case LocalActionType.SendWhatsApp:
        return {
          label: 'Send WhatsApp Message',
          value: LocalActionType.SendWhatsApp,
          icon: () => <WhatsappIcon />,
        }
      case LocalActionType.SubscribeToCampaign:
        return {
          label: 'Subscribe contact to campaign',
          value: LocalActionType.SubscribeToCampaign,
          icon: 'input',
        }
      case LocalActionType.Topup:
        return {
          label: 'Send Airtime Top-up to contact',
          value: LocalActionType.Topup,
          icon: 'account_balance_wallet',
        }
      case LocalActionType.Transfer:
        return {
          label: 'Transfer call to someone',
          value: LocalActionType.Transfer,
          icon: 'phone',
        }
      case LocalActionType.Unsubscribe:
        return {
          label: 'Unsubscribe contact from campaign',
          value: LocalActionType.Unsubscribe,
          icon: 'unsubscribe',
        }
      case LocalActionType.UnsubscribeAll:
        return {
          label: 'Unsubscribe contact from all campaigns',
          value: LocalActionType.UnsubscribeAll,
          icon: 'unsubscribe',
        }
      case LocalActionType.UpdateContact:
        return {
          label: 'Update contact',
          value: LocalActionType.UpdateContact,
          icon: 'contacts',
        }
      case LocalActionType.PlayAudio:
        return {
          label: 'Play audio message',
          value: LocalActionType.PlayAudio,
          icon: 'audiotrack',
        }

      default:
        return cannot(type)
    }
  })
